import React, { useEffect, useState } from "react"
import { Button, Input, Select } from "antd"
import { loadScriptScenes } from "./loadScriptScenes"
import { identifyProductionElements, addProductionElements } from "./aiApi"

import ProductionElementSuggestions from "./ProductionElementSuggestions"
import { approvedItems, initialRows } from "./productionSuggestionReview"
const COST = 2 // display copy; the server sets the real cost (backend/src/lib/ai-credits.js)

// AI suggests production elements for one saved scene; only items the user ticks are added to Production.
const ProductionElements = ({ scripts, scriptsLoading, onCreditsChanged }) => {
  const [scriptId, setScriptId] = useState()
  const [sceneId, setSceneId] = useState()
  const [scenes, setScenes] = useState([])
  const [scenesLoading, setScenesLoading] = useState(false)
  const [notes, setNotes] = useState("")
  const [running, setRunning] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [stale, setStale] = useState(false)
  const [result, setResult] = useState(null) // server response
  const [rows, setRows] = useState([])       // editable copy: {name, category, source, quote, reason, selected, ...}
  const [saved, setSaved] = useState(null)

  useEffect(() => {
    setSceneId(undefined); setScenes([]); setResult(null)
    if (!scriptId) return
    let off = false
    setScenesLoading(true)
    loadScriptScenes(scriptId).then((s) => !off && setScenes(s)).catch(() => !off && setScenes([])).finally(() => !off && setScenesLoading(false))
    return () => { off = true }
  }, [scriptId])
  useEffect(() => { setResult(null); setSaved(null); setStale(false) }, [sceneId])

  const identify = async () => {
    setRunning(true); setError(""); setSaved(null); setStale(false)
    try {
      const data = await identifyProductionElements({ scriptId, sceneId, notes })
      setResult(data)
      setRows(initialRows(data.suggestions))
      onCreditsChanged?.()
    } catch (e) { setError(e.message) } finally { setRunning(false) }
  }

  const add = async () => {
    const items = approvedItems(rows, result.inventory || [], result.sceneId)
    if (!items.length) return
    setSaving(true); setError("")
    try {
      const data = await addProductionElements({ scriptId, sceneId, revision: result.revision, inventoryRevision: result.inventoryRevision, items })
      setSaved(data)
      setResult((r) => ({ ...r, revision: data.revision, inventory: data.inventory || r.inventory, inventoryRevision: data.inventoryRevision || r.inventoryRevision }))
      setRows((rs) => rs.map((r) => ({ ...r, selected: false })))
      setStale(!data.inventory)
    } catch (e) {
      if (e.status === 409) setStale(true)
      setError(e.message)
    } finally { setSaving(false) }
  }

  const chosen = result ? approvedItems(rows, result.inventory || [], result.sceneId).length : 0
  const sceneNumber = scenes.find((s) => s.id === sceneId)?.index

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h3 className="font-bold text-[18px]">Production Elements<span className="dashboard-muted font-normal text-[14px]"> · {COST} credits</span></h3>
        <p className="dashboard-muted text-[14px] mt-1">AI reads one scene and suggests production elements. You choose which ones to add to the screenplay's Production section.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="min-w-0">
          <label className="block font-bold text-[14px] mb-2">Choose script</label>
          <Select aria-label="Choose script" size="large" className="w-full" placeholder="Choose script" showSearch optionFilterProp="label" loading={scriptsLoading}
            value={scriptId} onChange={setScriptId} notFoundContent={scriptsLoading ? "Loading scripts…" : "No scripts found"}
            options={scripts.map((s) => ({ value: s._id, label: s.title || "Untitled Script" }))} />
        </div>
        <div className="min-w-0">
          <label className="block font-bold text-[14px] mb-2">Choose scene</label>
          <Select aria-label="Choose scene" size="large" className="w-full" placeholder="Choose scene" disabled={!scriptId} loading={scenesLoading}
            value={sceneId} onChange={setSceneId} notFoundContent={scenesLoading ? "Loading scenes…" : "No scenes found"}
            options={scenes.map((s) => ({ value: s.id, label: `${s.index}. ${s.heading}` }))} />
        </div>
      </div>
      <div>
        <label htmlFor="pe-notes" className="block font-bold text-[14px] mb-2">Instructions <span className="dashboard-muted font-normal">(optional)</span></label>
        <Input.TextArea id="pe-notes" rows={3} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Identify all props, vehicles and wardrobe required for this scene." />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="primary" size="large" className="rounded-full font-bold" disabled={!scriptId || !sceneId || running} loading={running} onClick={identify}>
          Identify Elements · {COST} credits
        </Button>
        <span className="dashboard-muted text-[13px]">This request costs {COST} credits. Nothing is added until you approve it.</span>
      </div>
      {error && <div role="alert" className="text-red-600 text-[14px]">{error}{stale && <> <Button size="small" className="ml-2" onClick={identify} disabled={running}>Refresh results</Button></>}</div>}

      {result && <section aria-label="Suggested elements" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="font-bold text-[16px]">Suggestions for Scene {sceneNumber} · {result.sceneHeading}</h4>
          {result.simulated && <span className="dashboard-muted text-[12px]">Simulated answer (AI not called)</span>}
        </div>
        <ProductionElementSuggestions result={result} rows={rows} onChange={setRows} disabled={running || saving || stale} />
        {stale && !error && <Button onClick={identify} disabled={running || saving}>Refresh results</Button>}
        <Button type="primary" size="large" disabled={!chosen || saving || stale} loading={saving} onClick={add}>Add Approved{chosen ? ` (${chosen})` : ""}</Button>
        {saved && <p role="status" className="text-[14px]" style={{ color: "var(--dashboard-primary)" }}>
          {saved.added.length + saved.linked.length ? `Added to Production: ${[...saved.added, ...saved.linked].join(", ")}.` : "Nothing new to add."}
          {saved.skipped.length ? ` Already there: ${saved.skipped.join(", ")}.` : ""} Open Production in the editor to see and edit them.
        </p>}
        <p className="dashboard-muted text-[12px]">Credits are not deducted during the trial.</p>
      </section>}
    </div>
  )
}

export default ProductionElements
